import {
  CompanyAcronymsEnum,
  PermissionEnum,
  Role,
  RoleByTime,
  RoleMapType,
  SecurityGroupName,
  TranslationTextMap,
} from '@orgbrain/roles';
import { MongoId } from './mongoId';
import { CredentialEnum, IdentityType } from './session';
import { IUser } from './user';
import { EarlyAccessEnum } from './earlyAccess';
import { PolicyName, RuleName } from './org-auth-constants';
import { TimeEnum } from './times';

export enum ImplicitVotingSettings {
  IMPLICIT_FOR = 'IMPLICIT_FOR',
  NO_IMPLICIT_FOR = 'NO_IMPLICIT_FOR',
}

export interface PackagePrice {
  price: number;
  currency: string;
  isAnnualPrice: boolean;
  validUntil?: Date;
}

export enum AgendaItemStylesSettings {
  AGENDA_STYLE_NUMBER = 'AGENDA_STYLE_NUMBER',
  AGENDA_STYLE_ITEM_NUMBER = 'AGENDA_STYLE_ITEM_NUMBER',
  AGENDA_STYLE_CONSECUTIVE_NUMBER = 'AGENDA_STYLE_CONSECUTIVE_NUMBER',
  AGENDA_STYLE_ITEM_CONSECUTIVE_NUMBER = 'AGENDA_STYLE_ITEM_CONSECUTIVE_NUMBER',
  AGENDA_STYLE_NUMBER_MEETINGN_YEAR = 'AGENDA_STYLE_NUMBER_MEETINGN_YEAR',
  AGENDA_STYLE_ITEM_NUMBER_MEETINGN_YEAR = 'AGENDA_STYLE_ITEM_NUMBER_MEETINGN_YEAR',
  AGENDA_STYLE_ITEM_CONSECUTIVE_NUMBER_MEETINGN_YEAR = 'AGENDA_STYLE_ITEM_CONSECUTIVE_NUMBER_MEETINGN_YEAR',
  AGENDA_STYLE_ITEM_AND_SHORT_FORM_YEAR = 'AGENDA_STYLE_ITEM_AND_SHORT_FORM_YEAR',
  AGENDA_STYLE_CONSECUTIVE_ITEM_AND_SHORT_FORM_YEAR = 'AGENDA_STYLE_CONSECUTIVE_ITEM_AND_SHORT_FORM_YEAR',
  AGENDA_STYLE_ITEM_NO_NUMBER_AT_ALL = 'AGENDA_STYLE_ITEM_NO_NUMBER_AT_ALL',
}

export enum OrgLanguage {
  EN = 'EN',
  NO = 'NO',
  SE = 'SE',
}

export enum OrgJurisdiction {
  NO = 'NO',
  SE = 'SE',
  DK = 'DK',
}

export type UserManualId = {
  comment?: string;
  history: [{}];
  createdBy: string;
  createdAt: string;
  active: boolean;
};

// This corresponds to the OrganizationUserModel
export type IUserRoles = {
  _id: MongoId;
  organization: MongoId;
  user: MongoId | IUser;
  roles: Role[];
  rolesByTime: RoleByTime[];
  manualId: UserManualId;
  invited: boolean;
};

export enum DiscountType {
  DISCOUNT_CASH_TOTAL = 'DISCOUNT_CASH_TOTAL',
  DISCOUNT_PERCENT_TOTAL = 'DISCOUNT_PERCENT_TOTAL',
  DISCOUNT_USER_COUNT_GENERAL = 'DISCOUNT_USER_COUNT_GENERAL',
  DISCOUNT_USER_COUNT_SUBGROUP = 'DISCOUNT_USER_COUNT_SUBGROUP',
  DISCOUNT_USER_COUNT_ROLE_BASED = 'DISCOUNT_USER_COUNT_ROLE_BASED',
}

export interface DiscountItem {
  discountType: DiscountType;
  discountAmount: number;
  discountDurationMonths: number;
  discountAddedTimestamp: Date;
  discountRole: Role;
  dicountAmountCalculated?: number;
}

// todo check if there was no changes and all and SKIP
export enum SubscriptionChangeType {
  CHANGE_USER_COUNT = 'CHANGE_USER_COUNT',
  DD_COUNT_CHANGE = 'DD_COUNT_CHANGE',
  ORDER_SUBSCRIPTION = 'ORDER_SUBSCRIPTION',
  DATE_CHANGE = 'DATE_CHANGE',
  CLOSE_SUBSCRIPTION = 'CLOSE_SUBSCRIPTION',
  COMMENT_CHANGE = 'COMMENT_CHANGE',
  OTHER_CHANGE = 'OTHER_CHANGE',
}

export enum BillingPackageID {
  TEST = 'TEST',
  TEST2024 = 'TEST2024',
  FREEMIUM = 'FREEMIUM',
  SHAREHOLDER_BASIS = 'SHAREHOLDER_BASIS',
  SHAREHOLDER_PRO = 'SHAREHOLDER_PRO',
  DEPENDENT = 'DEPENDENT',
  SUSPENDED = 'SUSPENDED',
  GRUNDER = 'GRUNDER',
  SMALL_BUSINESS = 'SMALL_BUSINESS',
  FOUNDER = 'FOUNDER',
  SMALL_BUSINESS2 = 'SMALL_BUSINESS2',
  BASIS = 'BASIS',
  ENTERPRISE = 'ENTERPRISE',
  BOARDSCHOOL = 'BOARDSCHOOL',
  ACADEMY_2025 = 'ACADEMY_2025',
  BOARD_EVALUATION = 'BOARD_EVALUATION',
  NIF_START = 'NIF_START',
  NIF_PRO = 'NIF_PRO',
  FOUNDER2024 = 'FOUNDER2024',
  SMALL_BUSINESS2024 = 'SMALL_BUSINESS2024',
  STANDARD2024 = 'STANDARD2024',
  DD_SUBSCRIPTION = 'DD_SUBSCRIPTION',
  SIGNATURE_PACKAGE = 'SIGNATURE_PACKAGE',
  AI_TRANSCRIPTION_PACKAGE = 'AI_TRANSCRIPTION_PACKAGE',
  SECURITY_PRO = 'SECURITY_PRO',
}

export enum ReinvoicingRadioButtonType {
  YES = 'YES',
  NO = 'NO',
  ASK = 'ASK',
}

export interface OrderedSubscriptionDetailBase {
  subscriptionEnumKey: BillingPackageID;
  validUntil: Date;
  isMainSubscription: boolean;
}

export interface OrderedSubscriptionDetail extends OrderedSubscriptionDetailBase {
  _id?: MongoId;
  orderedAt: Date;
  startDate: Date;
  isAnnualPrice: boolean;
  userCount: number;
  ddProcessesCount?: number;
  userSubgrCount: number;
  storage: number;
  requesterId: string;
  requesterEmail: string;
  customPackagePrice?: number;
  generalInvoiceComment?: string;
  internalInvoiceComment?: string;
  customUserPrice?: number;
  userPrices?: PackagePrice[];
  packagePrices?: PackagePrice[];
  subgroupUserPrices?: PackagePrice[];
  customSubgroupUserPrice?: number;
  daughterCompanies?: string[];
  parentOrganization?: any;
  isEnterpriseParent?: boolean;
  reinvoicingRadioButton: ReinvoicingRadioButtonType;
  subscriptionReinvoicingDate?: Date;
  discounts?: DiscountItem[];
  packagePrice?: number;
  payableUsers?: number;
  payableUsersMoneySubtotal?: number;
  payableSubgroupUsers?: number;
  payableSubgroupUsersMoneySubtotal?: number;
  totalPriceForDDProcesses?: number;
  extraUsersMoneySubtotal?: number;
  extraSubgroupUsersMoneySubtotal?: number;
  totalMoneySubtotal?: number;
  diffUsersNormal?: number;
  diffUsersSubgr?: number;
  updatedAt?: Date;
}

export enum BillingProductType {
  SUBSCRIPTION_PACKAGE = 'SUBSCRIPTION_PACKAGE',
  SUBSCRIPTION_PACKAGE_UPGRADE = 'SUBSCRIPTION_PACKAGE_UPGRADE',
  SUBSCRIPTION_PACKAGE_USER_EXTENSION = 'SUBSCRIPTION_PACKAGE_USER_EXTENSION',
  SUBSCRIPTION_PACKAGE_REMAINDER = 'SUBSCRIPTION_PACKAGE_REMAINDER',
  USER_PACKAGE = 'USER_PACKAGE',
  SUBGROUP_USER_PACKAGE = 'SUBGROUP_USER_PACKAGE',
  ADDITIONAL_USER_PACKAGE = 'ADDITIONAL_USER_PACKAGE',
  ADDITIONAL_SUBGROUP_USER_PACKAGE = 'ADDITIONAL_SUBGROUP_USER_PACKAGE',
  DD_PROCESSES_GROUPS = 'DD_PROCESSES_GROUPS',
  DATAROOM_STORAGE = 'DATAROOM_STORAGE',
  SIGNING_DOCUMENTS = 'SIGNING_DOCUMENTS',

  USERS_INCLUDED_IN_SUBSCRIPTION = 'USERS_INCLUDED_IN_SUBSCRIPTION',
  FREE_USERS = 'FREE_USERS',
  FREE_SUBGROUP_USERS = 'FREE_SUBGROUP_USERS',
  FREE_SHAREHOLDER_USERS = 'FREE_SHAREHOLDER_USERS',
}

export enum ExtraPaidService {
  SIGNATURE_CRIIPTO = 'SIGNATURE_CRIIPTO',
  AI_SERVICE = 'AI_SERVICE',
  STORAGE = 'STORAGE',
}

export interface ProductSchema {
  productType: BillingProductType | DiscountType | ExtraPaidService | CredentialEnum;
  pricePerItemNOK: number;
  priceTotalNOK: number;
  quantity: number;
  additionalData?: string;
  subscriptionId?: string;
  paymentPeriodFrom?: Date;
  paymentPeriodTo?: Date;
  stringComment?: string;
  invoiceStat?: string;
  anyData?: any;
}

export interface PaidExtraService {
  _id: string | MongoId;
  userId?: string | MongoId;
  direntId?: string | MongoId;
  quantity?: number;
  paidServiceType?: string | ExtraPaidService | CredentialEnum;
  createdAt?: Date;
}

export interface OrganizationSubscription {
  _id: string;
  organization: string;
  packages: OrderedSubscriptionDetail[];
  transientInvoiceState?: ProductSchema[];
  tripletexClientId: string;
  organizationName?: string;
  orgId?: string;
  mainPackageId?: BillingPackageID;
  currency: number;
  invoiceEmail: string;
  email?: string;
  reference: string;
  invoiceSendMethod: string;
  name?: string;
  organizationNumber?: string;
  sendUpdatesFlag: boolean;
  vatType?: number;
  accountManager: string;
  overdueNoticeEmail: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: number;
  language: string;
  invoicesDueIn: number;
  emailAttachmentType: string;
  includedSignaturesPerYear: number;
  pricePerExtraSignature: number;
  includedStorageGb: number;
  pricePerGbPerMonth: number;
  pricePerBankIdIdentification: number;
  pricePerSmsIndetification: number;
  createdAt?: Date;
}

export type OrganizationConnection = {
  connectionName: string;
  organizations: MongoId[];
  _id: MongoId;
};

export type RuleParams = {
  inactivityPeriod?: TimeEnum; //
  activityPeriod?: TimeEnum; //
  credentials2FA?: CredentialEnum[]; // Alternatives for 2FA
  credentialsID?: CredentialEnum[]; // Alternatives for ID
  assertLoginCredentials?: CredentialEnum[]; // Alternatives for require Session credentials.  NO time limit. Used to assert user has logged in with one of these credentials
};

type SecurityRuleOverride = {
  ruleName: RuleName;
  params: RuleParams;
};

export type SecurityGroupOveride = {
  [securityGroupName in SecurityGroupName]?: SecurityRuleOverride;
}; // override params for a security group for the user

export type GroupFilePermissions = { download: boolean; watermark: boolean };
export type GroupFilePermissionsMap = { [group: string]: GroupFilePermissions };
export type OptionalPermissionSecurityGroupMap = { [perm in PermissionEnum]?: SecurityGroupName };

export type OrganizationPolicy = {
  assertLoginCredentials: CredentialEnum[];
  cannedPolicyName: PolicyName; // canned Policy name
  overrides: SecurityGroupOveride;
  permisionSecurityGroupOverrideMap: OptionalPermissionSecurityGroupMap;
};

/**
 * IOrganization represents the database schema for organizations.
 * All fields here are persisted in the database.
 * For computed/frontend-only fields, see the Organization class in lib-admin.
 */
export interface IOrganization {
  // Core identifiers
  _id?: any;
  orgId: string;
  name: string;
  clientId: string;
  hideSigningTextForRoles?: boolean;

  orderId?: MongoId;
  createdById?: MongoId;

  // Hierarchy and relationships
  parentOrgNumber?: string;
  parentOrgNumberReporting?: string;
  organizationGroupParentId?: MongoId;
  parentOrganization?: MongoId;
  childOrgNumbers?: string[];
  organizationConnectionDefinitions?: OrganizationConnection[];

  // Contact and location
  website?: string;
  phone?: string;
  city?: string;
  address?: string;
  postnr?: string;
  email?: string;
  invoiceEmail?: string;

  // Company metadata
  type?: CompanyAcronymsEnum;
  established?: string;
  language?: OrgLanguage;
  jurisdiction?: OrgJurisdiction;
  isPrivateOrg?: boolean;
  sportsOrgId?: string;
  hubspotOrgId?: string;
  activeCampaignId?: string;

  // Status flags
  deleted?: boolean;
  suspendedAt?: Date;
  demoMeetingsCreated?: boolean;

  // Timestamps
  createdAt?: Date;
  updatedAt?: Date;

  // Permissions and access
  supportPermission?: boolean;
  openaiPermission?: boolean;
  roleMapTweaks?: RoleMapType;
  requiredId?: IdentityType[];

  earlyAccess?: { [earlyAccessKey in EarlyAccessEnum]: boolean };

  // Voting and meeting settings
  extraSignaturesRequired?: Role[];
  implicitVoting?: ImplicitVotingSettings;
  agendaItemStyle?: AgendaItemStylesSettings;
  agendaItemStyleGA?: AgendaItemStylesSettings;
  agendaItemStyle3?: AgendaItemStylesSettings;
  agendaItemStyle4?: AgendaItemStylesSettings;

  // Display settings
  simplePDFFlag?: boolean;
  shareClassDistributionFlag?: boolean;
  allowBlankVotingInGA?: boolean;
  showWhoHasVoted?: boolean;
  showEngagementMeter?: boolean;
  showAgendaItemResponsible?: boolean;
  agendaItemUseStartTimeInsteadOfDuration?: boolean;
  agendaItemShowTimeInMinutes?: boolean;
  showAgendaItemLogs?: boolean;
  showAgendaSpeakingList?: boolean;
  showProcedureTypeInPdf?: boolean;
  showAgendaComments?: boolean;
  hideAgendaTabForParticipants?: boolean;

  // Branding
  logo?: OrgAndClientLogoType;

  // File permissions
  authPolicy?: OrganizationPolicy;
  defaultFilePermissionsMap?: GroupFilePermissionsMap;

  // Translation overrides
  roleTranslateTexts?: TranslationTextMap;

  // AI and recording features
  transcriptionsStatisticsEnabled?: boolean;
  minutesGenerationEnabled?: boolean;
  recordingEnabled?: boolean;
  transcriptionEnabled?: boolean;

  // Subscription and billing (computed but saved to DB)
  selectedPackage?: string;
  validFromSelectedPackage?: Date;
  validToSelectedPackage?: Date;
  numUsersSelectedPackage?: number;
  totalUniqueUsers?: number;
  currendRollingPeriodStart?: Date;
  currendRollingPeriodEnd?: Date;
  amountSelectedPackage?: number;
  previousSelectedMainPackageId?: string;
  payingOrgSubscriptions?: OrderedSubscriptionDetailBase[];
  payingOrgSubscriptionsExpiration?: Date;
  payingOrgMainPackageId?: BillingPackageID;
  selectedMainPackageId?: BillingPackageID;
  totalExpectedAnnualPayment?: number;

  //Billing / subscription
  previousSelectedPackage?: string;
  firstOrderedPaidSubscription?: Date;
  isAnnualPriceSelectedPackage?: boolean;
  paidUsersCount?: number;
  totalUniqueUsersExlShareholders?: number;
  uniqeUsersInSubgroups?: number;
  usersOrderedAndPaidDiff?: number;
  //Internal / operational
  lastModifiedBy?: string;
  source_key?: string;
  reference?: string;
  accountManager?: string;
  onboardingDurationDays?: number;
}

/*
 const orgs = await OrganizationModel.find(}).populate({
        path: 'members',
        populate: 'user',
      })
*/
export interface IOrganizationWithRoles extends IOrganization {
  members: IUserRoles[];
}

export type OrgAndClientLogoType = {
  url: string;
  urlForEmail: string;
  folderPath: string;
  extension: string;
  name: string;
  fileSize: number;
};

export function notSaveJustAssignSubscriptionsIntoOrg(
  organization: IOrganization,
  allActiveSubscriptions: OrderedSubscriptionDetailBase[]
): string[] {
  let roleMapExpiration = null;
  let mainPack = null;
  organization.payingOrgSubscriptions = allActiveSubscriptions.map((pack) => {
    if (pack.isMainSubscription) {
      mainPack = pack.subscriptionEnumKey;
    }

    if (
      pack.validUntil &&
      (!roleMapExpiration || new Date(roleMapExpiration) > new Date(pack.validUntil))
    ) {
      // For example, legacy TEST subscription can be in the past, but still valid. we only assign roleMapExpiration for future dates
      if (new Date(pack.validUntil) > new Date()) {
        roleMapExpiration = pack.validUntil;
      }
    }

    return {
      subscriptionEnumKey: pack.subscriptionEnumKey,
      validUntil: pack.validUntil,
      isMainSubscription: pack.isMainSubscription,
    };
  });

  organization.payingOrgSubscriptionsExpiration = roleMapExpiration;

  // selectedMainPackageId is used in a few places to assert the main subscription
  // The selectedPackage line is used ONLY for reporting. It comes in a translated format.
  // Here we don't translate it to avoid messing around with translation service in a code for user roles.
  // Translation happens in orderpackagehelper and works 99% of time
  // The below "untranslated" version may still arise in rare situations, like expiration of Parent subscription

  if (organization.parentOrganization || organization.organizationGroupParentId) {
    organization.selectedMainPackageId = BillingPackageID.DEPENDENT;
    organization.selectedPackage = `${organization.selectedMainPackageId} child to ${organization.parentOrganization || organization.organizationGroupParentId}`;
  } else {
    organization.selectedMainPackageId = mainPack || BillingPackageID.FREEMIUM;
    organization.selectedPackage = organization.selectedMainPackageId;
  }

  // This key is used to figure out the main subscription of the organization that handles the payment (current org or parent org for subgroups/child)
  organization.payingOrgMainPackageId = mainPack || BillingPackageID.FREEMIUM;

  // It always returns array of keys which are the fields that we update in this function.
  // If we start updating more fields - need to update this array
  return [
    'payingOrgSubscriptions',
    'payingOrgSubscriptionsExpiration',
    'selectedMainPackageId',
    'selectedPackage',
    'payingOrgMainPackageId',
  ];
}
